import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import apiClient from "../../core/config/api";
import PaymentResult, { ResultVariant } from "./PaymentResult";
import { useHubSpotScript } from "../../utils/useHubSpotScript";

// "processing": paid with a delayed method (US bank account) that is still
// clearing - a few business days; the webhook adds the credits when it does.
type PaymentStatus = "success" | "failed" | "cancelled" | "pending" | "processing" | string;

type PaymentStatusResponse = {
  success: boolean;
  session_id: string;
  status: PaymentStatus;
  payment_status?: string | null;
  plan_id?: number | null;
  subscription_id?: string | null;
  amount?: string | number | null;
  currency?: string | null;
  // Credit top-up (one-time bonus credits) rather than a plan purchase.
  is_topup?: boolean;
  credits?: number | null;
};

// The webhook that finalizes the payment may land a moment after Stripe
// redirects back, so we poll a few times while the status is still "pending".
const MAX_POLLS = 5;
const POLL_DELAY_MS = 2000;

const PaymentSuccess = () => {
  useHubSpotScript();

  const sessionId = new URLSearchParams(window.location.search).get("session_id");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [result, setResult] = useState<PaymentStatusResponse | null>(null);
  const pollsRef = useRef(0);
  const navigate = useNavigate();

  // A failed or cancelled payment has its own page.
  useEffect(() => {
    if (result?.status === "failed") {
      navigate(`/payment/failed?session_id=${encodeURIComponent(sessionId || "")}`, { replace: true });
    } else if (result?.status === "cancelled") {
      navigate("/payment/cancel", { replace: true });
    }
  }, [result, navigate, sessionId]);

  // Top-up / Pay As You Go paid: go on to the customer panel by itself.
  useEffect(() => {
    if (result?.status !== "success" || !result?.is_topup) return;
    const timer = setTimeout(() => navigate("/dashboard", { replace: true }), 3000);
    return () => clearTimeout(timer);
  }, [result, navigate]);

  useEffect(() => {
    if (!sessionId) {
      setLoading(false);
      setError("Missing payment session. We couldn't verify your payment.");
      return;
    }

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;

    const check = async () => {
      try {
        const { data } = await apiClient.get<PaymentStatusResponse>(
          `/stripe-payment/status/${sessionId}`
        );
        if (cancelled) return;

        // Keep polling while the webhook hasn't finalized the payment yet.
        if (data.status === "pending" && pollsRef.current < MAX_POLLS) {
          pollsRef.current += 1;
          timer = setTimeout(check, POLL_DELAY_MS);
          return;
        }

        setResult(data);
        setLoading(false);
      } catch (e: any) {
        if (cancelled) return;
        setError(
          e?.response?.data?.detail ||
            e?.response?.data?.message ||
            "Failed to verify payment status. Please check your payment history."
        );
        setLoading(false);
      }
    };

    check();

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [sessionId]);

  // Map the current state to the result-page view model.
  const view: {
    variant: ResultVariant;
    title: string;
    message: string;
    amount?: string | null;
    primary?: { label: string; to: string };
    secondary?: { label: string; to: string };
  } = (() => {
    if (loading) {
      return {
        variant: "loading",
        title: "Verifying your payment…",
        message: "Please wait while we confirm your transaction with Stripe.",
      };
    }

    if (error) {
      return {
        variant: "error",
        title: "Verification failed",
        message: error,
        primary: { label: "Go to Payment History", to: "/payment-history" },
      };
    }

    const status = result?.status;

    if (status === "success" && result?.is_topup) {
      const credits = Number(result.credits || 0);
      return {
        variant: "success",
        title: "Credits Added!",
        message: `${credits ? credits.toLocaleString("en-US") : "Your"} credits have been added to your balance. Taking you to your dashboard...`,
        primary: { label: "Go to Dashboard", to: "/dashboard" },
      };
    }

    if (status === "processing") {
      return {
        variant: "pending",
        title: "Payment Processing",
        message: result?.is_topup
          ? "Your bank is confirming the payment. This can take a few business days - your top-up credits will be added as soon as it clears."
          : "Your bank is confirming the payment. This can take a few business days - your plan and credits will be activated as soon as it clears.",
        primary: { label: "Go to Dashboard", to: "/dashboard" },
        secondary: { label: "Payment History", to: "/payment-history" },
      };
    }

    if (status === "pending" && result?.is_topup) {
      return {
        variant: "pending",
        title: "Payment Processing",
        message: "We've received your payment. Your top-up credits will appear shortly.",
        primary: { label: "Back to Email Verifier", to: "/email-verifier" },
      };
    }

    if (status === "success") {
      return {
        variant: "success",
        title: "Payment Successful!",
        message: "Your subscription is now active and your credits have been added.",
        primary: { label: "Get Started", to: "/dashboard" },
      };
    }

    if (status === "pending") {
      return {
        variant: "pending",
        title: "Payment Processing",
        message:
          "We've received your payment and are finalizing your subscription. Your credits will appear shortly.",
        primary: { label: "Go to Dashboard", to: "/dashboard" },
      };
    }

    // failed / cancelled / anything else
    return {
      variant: "error",
      title: status === "cancelled" ? "Payment Cancelled" : "Payment Failed",
      message:
        status === "cancelled"
          ? "Your payment was cancelled. You have not been charged."
          : "We couldn't complete your payment. You have not been charged.",
      primary: { label: "Back to Pricing", to: "/pricing" },
    };
  })();

  return (
    <PaymentResult
      variant={view.variant}
      title={view.title}
      message={view.message}
      amount={view.amount}
      primary={view.primary}
      secondary={view.secondary}
    />
  );
};

export default PaymentSuccess;
